import { useEffect, useState } from 'react';
import type { CanvasActionItem, CanvasBlock, CanvasDocument, CanvasTableBlock } from '@orqforge/shared';
import { ConfirmDialog } from '../ui/ConfirmDialog';

function toneClass(tone?: string): string {
  if (tone === 'ok') return 'text-green-600';
  if (tone === 'warn') return 'text-amber-600';
  if (tone === 'bad') return 'text-red-600';
  return '';
}

export function CanvasPane({
  document,
  executing,
  onAction,
}: {
  document: CanvasDocument | null;
  executing?: boolean;
  onAction: (event: { toolKey: string; arguments: Record<string, unknown> }) => void;
}) {
  const [tableQuery, setTableQuery] = useState('');
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [pending, setPending] = useState<CanvasActionItem | null>(null);

  useEffect(() => {
    setTableQuery('');
    setSortCol(null);
    setSortDir('asc');
    setExpandedRow(null);
  }, [document]);

  if (!document) return null;

  function visibleRows(block: CanvasTableBlock): (string | number)[][] {
    const query = tableQuery.trim().toLowerCase();
    let rows = block.rows;
    if (query) rows = rows.filter((row) => row.some((cell) => String(cell).toLowerCase().includes(query)));
    if (sortCol !== null) {
      const col = sortCol;
      const dir = sortDir === 'asc' ? 1 : -1;
      rows = [...rows].sort((a, b) => String(a[col] ?? '').localeCompare(String(b[col] ?? ''), undefined, { numeric: true }) * dir);
    }
    return rows;
  }

  function runAction(item: CanvasActionItem) {
    if (item.confirm) {
      setPending(item);
      return;
    }
    onAction({ toolKey: item.toolKey, arguments: item.arguments ?? {} });
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <h2 className="text-lg font-semibold leading-tight">{document.title}</h2>
      {document.subtitle && <p className="mt-1 text-sm text-gray-500">{document.subtitle}</p>}
      {document.kpis && document.kpis.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {document.kpis.map((kpi) => (
            <div key={kpi.label} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-400">{kpi.label}</div>
              <div className={`text-sm font-semibold ${toneClass(kpi.tone)}`}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-col gap-4">
        {document.blocks.map((block, index) => (
          <BlockView
            key={index}
            block={block}
            tableQuery={tableQuery}
            onQuery={setTableQuery}
            sortCol={sortCol}
            sortDir={sortDir}
            onSort={(col) => {
              if (sortCol === col) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
              else {
                setSortCol(col);
                setSortDir('asc');
              }
            }}
            expandedRow={expandedRow}
            onExpand={setExpandedRow}
            visibleRows={visibleRows}
            executing={Boolean(executing)}
            onAction={runAction}
          />
        ))}
      </div>
      <ConfirmDialog
        open={Boolean(pending)}
        message={pending?.confirm ?? ''}
        confirmText={pending?.label ?? 'Confirm'}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          const item = pending;
          setPending(null);
          onAction({ toolKey: item.toolKey, arguments: item.arguments ?? {} });
        }}
      />
    </div>
  );
}

function BlockView(props: {
  block: CanvasBlock;
  tableQuery: string;
  onQuery: (value: string) => void;
  sortCol: number | null;
  sortDir: 'asc' | 'desc';
  onSort: (col: number) => void;
  expandedRow: string | null;
  onExpand: (key: string | null) => void;
  visibleRows: (block: CanvasTableBlock) => (string | number)[][];
  executing: boolean;
  onAction: (item: CanvasActionItem) => void;
}) {
  const { block } = props;
  if (block.type === 'table') {
    const rows = props.visibleRows(block);
    return (
      <div>
        {block.title && <h3 className="mb-2 text-sm font-semibold">{block.title}</h3>}
        <input
          className="mb-2 w-full max-w-xs rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800"
          placeholder="Filter rows"
          value={props.tableQuery}
          onChange={(event) => props.onQuery(event.target.value)}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {block.columns.map((col, index) => (
                  <th key={col} className="cursor-pointer select-none px-2 py-1" onClick={() => props.onSort(index)}>
                    {col}
                    {props.sortCol === index ? <span className="opacity-50">{props.sortDir === 'asc' ? ' ↑' : ' ↓'}</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const key = `${block.title ?? 't'}:${rowIndex}`;
                return (
                  <>
                    <tr key={key} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => props.onExpand(props.expandedRow === key ? null : key)}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-2 py-1">{cell}</td>
                      ))}
                    </tr>
                    {props.expandedRow === key && (
                      <tr key={`${key}-detail`}>
                        <td colSpan={block.columns.length}>
                          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
                            {block.columns.map((col, colIndex) => (
                              <span key={col} className="contents">
                                <dt className="text-gray-400">{col}</dt>
                                <dd>{row[colIndex]}</dd>
                              </span>
                            ))}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (block.type === 'timeline') {
    return (
      <div>
        {block.title && <h3 className="mb-2 text-sm font-semibold">{block.title}</h3>}
        <ul className="space-y-2 text-sm">
          {block.events.map((event, index) => (
            <li key={index}>
              <div className="text-[0.65rem] uppercase text-gray-400">{event.time} · {event.actor}</div>
              <div>{event.text}</div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (block.type === 'status') {
    return (
      <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
        <span className="text-gray-500">{block.label}</span>
        <span className={`font-semibold ${toneClass(block.tone)}`}>{block.value}</span>
      </div>
    );
  }
  if (block.type === 'markdown') {
    return <p className="whitespace-pre-wrap text-sm">{block.markdown}</p>;
  }
  if (block.type === 'actions') {
    return (
      <div>
        {block.title && <h3 className="mb-2 text-sm font-semibold">{block.title}</h3>}
        <div className="flex flex-wrap gap-2">
          {block.items.map((item) => (
            <button
              key={item.label}
              type="button"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
              data-testid={`canvas-action-${item.label}`}
              disabled={props.executing}
              onClick={() => props.onAction(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const _never: never = block;
  return _never;
}
